import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { captureAPI, handleAPIError } from '../services/api';
import {
  shouldExecuteWorkflowForSingleUpload,
  executableDocumentIdsFromBatch,
  duplicateChoiceFromUploadError,
} from '../utils/ingestionGuards';
import './UploadPage.css';

const VALID_TYPES = [
  'application/pdf',
  'image/jpeg', 'image/png', 'image/tiff', 'image/gif',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

const MAX_FILE_SIZE_MB = 50;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const UploadPage = () => {
  const [uploadStatus, setUploadStatus] = useState('idle');
  // idle | uploading | workflow-started | duplicate-flagged | batch-workflow-started | error | batch-ready | batch-uploading
  const [selectedFile, setSelectedFile] = useState(null);
  const [error, setError] = useState(null);
  const [documentId, setDocumentId] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  // Gate C block-and-choose: { file, existingDocument } when the picked file
  // matches a document the tenant already has.
  const [duplicatePrompt, setDuplicatePrompt] = useState(null);

  // Workflow selector
  const [workflows, setWorkflows] = useState([]);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState(null);
  const [loadingWorkflows, setLoadingWorkflows] = useState(true);

  // Batch upload state
  const [batchFiles, setBatchFiles] = useState([]);
  const [batchPriority, setBatchPriority] = useState('normal');
  const [batchDocCount, setBatchDocCount] = useState(0);

  const fileInputRef = useRef(null);
  const abortRef = useRef(false);
  const navigate = useNavigate();

  // ── Load published workflows on mount ──
  useEffect(() => {
    captureAPI.workflows.list()
      .then(res => {
        const published = (res.workflows || []).filter(w => w.is_published === true);
        setWorkflows(published);
        if (published.length === 1) setSelectedWorkflowId(published[0].id);
      })
      .catch(err => console.error('Failed to load workflows:', err))
      .finally(() => setLoadingWorkflows(false));
  }, []);

  // ── Helpers ──

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const resetState = useCallback(() => {
    abortRef.current = true;
    setUploadStatus('idle');
    setSelectedFile(null);
    setError(null);
    setDocumentId(null);
    setBatchFiles([]);
    setBatchPriority('normal');
    setBatchDocCount(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  // ── Core: upload file then execute workflow ──

  const handleFileSelected = useCallback(async (file, { overrideDuplicate = false } = {}) => {
    if (!file) return;

    if (!VALID_TYPES.includes(file.type)) {
      setError({ message: `Unsupported file type: ${file.type}. Supported: PDF, JPG, PNG, TIFF, GIF, XLSX, DOCX.` });
      setUploadStatus('error');
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setError({ message: `File too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximum size is ${MAX_FILE_SIZE_MB} MB.` });
      setUploadStatus('error');
      return;
    }

    if (!selectedWorkflowId) {
      setError({ message: 'Please select a processing workflow before uploading.' });
      setUploadStatus('error');
      return;
    }

    // Reset any previous state
    abortRef.current = false;
    setSelectedFile(file);
    setError(null);
    setDuplicatePrompt(null);
    setDocumentId(null);
    setUploadStatus('uploading');

    try {
      // Step 1: Upload — propagate workflowId so the capture_documents row
      // is created with workflow_id set (canonical extraction trigger,
      // Option C). Backend's /upload reads req.body.workflow_id.
      // overrideDuplicate (Gate C "Upload as new") proceeds past a duplicate block.
      console.log('[Upload] Uploading:', file.name, 'workflow:', selectedWorkflowId, overrideDuplicate ? '(override)' : '');
      const result = await captureAPI.uploadFile(file, { workflowId: selectedWorkflowId, overrideDuplicate });
      const doc = result?.captureDocument || result?.document || result?.capture_document;
      const docId = doc?.id || result?.documentId;

      if (!docId) {
        console.error('[Upload] No document ID in response:', result);
        throw new Error('Upload succeeded but no document ID was returned.');
      }

      console.log('[Upload] Uploaded, docId:', docId);
      setDocumentId(docId);

      // I1 Decision 9: duplicate-flagged documents do not get a workflow
      // execution — they wait in 'duplicate_pending_review' for user
      // review. Server already skipped the OCR-job enqueue; the UI
      // mirrors by not calling executeWorkflow at all.
      if (!shouldExecuteWorkflowForSingleUpload(result)) {
        console.log('[Upload] Duplicate-flagged — skipping workflow execution', result.ingestion);
        setUploadStatus('duplicate-flagged');
        setTimeout(() => navigate('/processing'), 2000);
        return;
      }

      // Step 2: Execute workflow
      const execRes = await captureAPI.workflows.execute(selectedWorkflowId, docId);
      if (!execRes.ok) throw new Error(execRes.error || 'Failed to start workflow');

      console.log('[Upload] Workflow started:', execRes);
      setUploadStatus('workflow-started');

      // Brief delay then navigate to processing monitor
      setTimeout(() => navigate('/processing'), 2000);

    } catch (err) {
      if (err.message === 'Cancelled') return;
      // Gate C block-and-choose: a live duplicate → offer Use existing / Upload as new.
      const existing = duplicateChoiceFromUploadError(err);
      if (existing) {
        console.log('[Upload] Duplicate detected — prompting for decision', existing);
        setDuplicatePrompt({ file, existingDocument: existing });
        setUploadStatus('idle');
        return;
      }
      console.error('[Upload] Error:', err);
      const errorInfo = handleAPIError(err, 'Upload');
      setError(errorInfo);
      setUploadStatus('error');
    }
  }, [navigate, selectedWorkflowId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Gate C: duplicate choice handlers ──
  const handleUseExisting = useCallback(() => {
    const existing = duplicatePrompt?.existingDocument;
    setDuplicatePrompt(null);
    // existingDocument.id is a CAPTURE document id (from the dedup match). View it
    // via the capture-side review route (/documents/:id → ReviewDetailPage, which
    // resolves a capture document id directly through capture-service + shows the
    // PDF and extracted fields). Do NOT use /viewer/:id — that is the LIBRARY
    // viewer (GET /api/documents/:id on library-backend), which has no row for a
    // capture id and 404s (diag a7c6963). Capture dups often have no library copy
    // (library_document_id NULL), so capture-side is the reliable destination.
    if (existing?.id) navigate(`/documents/${existing.id}`);
  }, [duplicatePrompt, navigate]);

  const handleUploadAsNew = useCallback(() => {
    const file = duplicatePrompt?.file;
    setDuplicatePrompt(null);
    if (file) handleFileSelected(file, { overrideDuplicate: true });
  }, [duplicatePrompt, handleFileSelected]);

  // ── Event handlers ──

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const allFiles = Array.from(e.dataTransfer.files).filter(f => VALID_TYPES.includes(f.type) && f.size <= MAX_FILE_SIZE_BYTES);
    const rejected = Array.from(e.dataTransfer.files).filter(f => f.size > MAX_FILE_SIZE_BYTES);
    if (rejected.length > 0) {
      setError({ message: `${rejected.length} file(s) exceeded ${MAX_FILE_SIZE_MB} MB limit and were skipped.` });
    }
    if (allFiles.length === 0) return;
    if (allFiles.length === 1 && rejected.length === 0) {
      handleFileSelected(allFiles[0]);
    } else {
      setBatchFiles(prev => [...prev, ...allFiles]);
      setUploadStatus('batch-ready');
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleFileInputChange = (e) => {
    const allFiles = Array.from(e.target.files).filter(f => VALID_TYPES.includes(f.type) && f.size <= MAX_FILE_SIZE_BYTES);
    if (allFiles.length === 0) return;
    if (allFiles.length === 1 && batchFiles.length === 0) {
      handleFileSelected(allFiles[0]);
    } else {
      setBatchFiles(prev => [...prev, ...allFiles]);
      setUploadStatus('batch-ready');
    }
  };

  const removeBatchFile = (index) => {
    setBatchFiles(prev => {
      const next = prev.filter((_, i) => i !== index);
      if (next.length === 0) setUploadStatus('idle');
      return next;
    });
  };

  const handleBatchUpload = async () => {
    if (batchFiles.length === 0) return;
    if (!selectedWorkflowId) {
      setError({ message: 'Please select a processing workflow before uploading.' });
      return;
    }
    setUploadStatus('batch-uploading');
    setError(null);
    try {
      // Step 1: Upload all files as a batch — propagate workflowId so each
      // capture_documents row is created with workflow_id set (canonical
      // extraction trigger, Option C). Backend's /upload/batch reads
      // req.body.workflow_id and applies it to every child row.
      const result = await captureAPI.batches.upload(batchFiles, {
        batchName: `Upload ${new Date().toLocaleString()}`,
        priority: batchPriority,
        workflowId: selectedWorkflowId,
      });
      console.log('[Upload] Batch upload complete:', result);

      // Step 2: Execute workflow for the non-duplicate subset.
      // I1 Decision 9: duplicate-flagged documents are filtered out —
      // they stay in 'duplicate_pending_review' until a user acts on them.
      // executableDocumentIdsFromBatch consults each document's status
      // field (set by the capture-app /upload/batch handler) and falls
      // back to the legacy document_ids array for older servers.
      const documentIds = executableDocumentIdsFromBatch(result);
      const totalReturned =
        result.documents?.length ?? result.document_ids?.length ?? 0;
      const flaggedCount = totalReturned - documentIds.length;
      if (flaggedCount > 0) {
        console.log(
          `[Upload] Batch upload: ${flaggedCount}/${totalReturned} document(s) flagged as duplicate ` +
            `and held (Processing > Held) — executing workflow for the remaining ${documentIds.length}`,
        );
      }
      if (documentIds.length === 0) {
        console.warn(
          '[UploadPage] Batch upload returned no executable document IDs ' +
          '(all flagged as duplicates, or server returned an unexpected shape); ' +
          'workflow execution will be skipped',
          result
        );
      } else {
        const execRes = await captureAPI.workflows.executeBatch(selectedWorkflowId, documentIds);
        console.log('[Upload] Batch workflow execution:', execRes);
      }

      setBatchDocCount(batchFiles.length);
      setUploadStatus('batch-workflow-started');

      // Brief delay then navigate to processing monitor (with batch filter)
      const batchNavId = result.batch_id;
      setTimeout(() => navigate(batchNavId ? `/processing?batch=${batchNavId}` : '/processing'), 2000);
    } catch (err) {
      console.error('[Upload] Batch upload failed:', err);
      const errorInfo = handleAPIError(err, 'Batch Upload');
      setError(errorInfo);
      setUploadStatus('error');
    }
  };

  // ── Derived state ──

  const isProcessing = ['uploading', 'workflow-started', 'duplicate-flagged', 'batch-workflow-started', 'batch-uploading'].includes(uploadStatus);
  const canSelectFile = !isProcessing;
  const isBatchMode = uploadStatus === 'batch-ready' || uploadStatus === 'batch-uploading';

  return (
    <div className="upload-page">
      <div className="card" style={{ maxWidth: '800px', margin: '0 auto' }}>
        <div className="card-header">
          <h2>Upload & Process Document</h2>
          <p style={{ color: '#6b7280', marginTop: '0.5rem' }}>
            Select a document and workflow to begin processing
          </p>
        </div>

        <div className="card-body">
          {/* Workflow selector */}
          <div className="upload-workflow-selector">
            <label>Processing Workflow</label>
            {loadingWorkflows ? (
              <div style={{ fontSize: '13px', color: '#9ca3af', padding: '8px 0' }}>Loading workflows...</div>
            ) : workflows.length === 0 ? (
              <div style={{ fontSize: '13px', color: '#dc2626', padding: '8px 0' }}>
                No published workflows found. <a href="/workflows" style={{ color: 'var(--stm-accent-info)' }}>Create one</a> first.
              </div>
            ) : (
              <select
                value={selectedWorkflowId || ''}
                onChange={(e) => setSelectedWorkflowId(e.target.value || null)}
                disabled={isProcessing}
              >
                {workflows.length > 1 && <option value="">Select a workflow...</option>}
                {workflows.map(w => (
                  <option key={w.id} value={w.id}>{w.name}</option>
                ))}
              </select>
            )}
          </div>
          {/* Drop Zone */}
          <div
            className={`up-dropzone${isDragOver ? ' up-dropzone--drag' : ''}${isProcessing ? ' up-dropzone--disabled' : ''}`}
            onDrop={canSelectFile ? handleDrop : undefined}
            onDragOver={canSelectFile ? handleDragOver : undefined}
            onDragLeave={canSelectFile ? handleDragLeave : undefined}
            onClick={() => canSelectFile && fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.tiff,.tif,.gif,.xlsx,.docx"
              multiple
              onChange={handleFileInputChange}
              style={{ display: 'none' }}
              disabled={isProcessing}
            />

            {isProcessing ? (
              <div className="up-dropzone-content">
                <div className="up-dropzone-icon">📄</div>
                <h3>{selectedFile?.name}</h3>
                <p className="up-dropzone-meta">
                  {formatFileSize(selectedFile?.size)} &bull; {selectedFile?.type}
                </p>
              </div>
            ) : (
              <div className="up-dropzone-content">
                <div className="up-dropzone-icon">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                </div>
                <h3>Drop documents here to begin</h3>
                <p style={{ color: '#6b7280', marginBottom: '0.75rem' }}>
                  or click to browse your files
                </p>
                <p style={{ color: '#9ca3af', fontSize: '0.8rem' }}>
                  PDF, JPG, PNG, TIFF, XLSX, DOCX &bull; Single file auto-processes &bull; Multiple files enable batch mode
                </p>
                {!selectedWorkflowId && workflows.length > 0 && (
                  <p style={{ color: '#d97706', fontSize: '0.8rem', marginTop: '0.5rem' }}>
                    Select a workflow above to begin processing.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Batch File List */}
          {(uploadStatus === 'batch-ready' || uploadStatus === 'batch-uploading') && batchFiles.length > 0 && (
            <div className="up-batch-panel">
              <div className="up-batch-header">
                <h3>{batchFiles.length} file{batchFiles.length !== 1 ? 's' : ''} selected</h3>
                <span className="up-batch-size">
                  {formatFileSize(batchFiles.reduce((sum, f) => sum + f.size, 0))} total
                </span>
              </div>
              <ul className="up-batch-list">
                {batchFiles.map((file, idx) => (
                  <li key={idx} className="up-batch-item">
                    <span className="up-batch-item-icon">📄</span>
                    <span className="up-batch-item-name">{file.name}</span>
                    <span className="up-batch-item-size">{formatFileSize(file.size)}</span>
                    {uploadStatus === 'batch-ready' && (
                      <button
                        className="up-batch-item-remove"
                        onClick={() => removeBatchFile(idx)}
                        title="Remove file"
                      >✕</button>
                    )}
                  </li>
                ))}
              </ul>
              <div className="up-batch-controls">
                <div className="up-batch-priority">
                  <label htmlFor="batch-priority">Priority:</label>
                  <select
                    id="batch-priority"
                    value={batchPriority}
                    onChange={(e) => setBatchPriority(e.target.value)}
                    disabled={uploadStatus === 'batch-uploading'}
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
                <div className="up-batch-actions">
                  <button
                    className="btn btn-secondary"
                    onClick={resetState}
                    disabled={uploadStatus === 'batch-uploading'}
                  >
                    Clear All
                  </button>
                  <button
                    className="btn btn-primary up-btn-batch-upload"
                    onClick={handleBatchUpload}
                    disabled={uploadStatus === 'batch-uploading'}
                  >
                    {uploadStatus === 'batch-uploading' ? (
                      <><div className="up-spinner-sm" style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: 8 }} /> Uploading...</>
                    ) : (
                      `Upload ${batchFiles.length} document${batchFiles.length !== 1 ? 's' : ''}`
                    )}
                  </button>
                </div>
              </div>
              {uploadStatus === 'batch-ready' && (
                <p className="up-batch-hint">
                  Drop more files to add them, or click the upload button to start batch processing.
                </p>
              )}
            </div>
          )}

          {/* Status Display.
              The Gate C duplicate prompt lives in this panel but the 409 handler
              resets uploadStatus to 'idle' (so the dropzone re-enables) — without
              the `duplicatePrompt ||` guard the panel would gate the prompt out
              and it would never render (the flash-then-gone bug). Keep the prompt
              visible whenever it is set, regardless of status. */}
          {(duplicatePrompt || (uploadStatus !== 'idle' && uploadStatus !== 'batch-ready' && uploadStatus !== 'batch-uploading')) && (
            <div className="up-status-panel">
              {/* Uploading */}
              {uploadStatus === 'uploading' && (
                <div className="up-status up-status--blue">
                  <div className="up-spinner" />
                  <span>Uploading {selectedFile?.name}...</span>
                </div>
              )}

              {/* Workflow started — single file */}
              {uploadStatus === 'workflow-started' && (
                <div className="up-status up-status--green">
                  <span>✓ Workflow started! Redirecting to processing monitor...</span>
                </div>
              )}

              {/* Duplicate flagged — single file (I1; copy updated in B4).
                  "review pending" was true and useless: it named a review that
                  had no surface anywhere in the product. It now names the place. */}
              {uploadStatus === 'duplicate-flagged' && (
                <div className="up-status up-status--blue">
                  <span>
                    ⚠ This matches a document already captured, so it was held rather
                    than processed. You can process it anyway or discard it on the
                    Processing page under Held. Redirecting...
                  </span>
                </div>
              )}

              {/* Gate C — duplicate block-and-choose (single file) */}
              {duplicatePrompt && (
                <div className="up-status up-status--blue up-dup-choice" role="dialog" aria-label="Duplicate document">
                  <div className="up-dup-choice__body">
                    <strong>This matches a document you already have</strong>
                    <p className="up-dup-choice__meta">
                      {duplicatePrompt.existingDocument.filename || 'Existing document'}
                      {duplicatePrompt.existingDocument.created_at
                        ? ` — uploaded ${new Date(duplicatePrompt.existingDocument.created_at).toLocaleDateString()}`
                        : ''}
                    </p>
                    <div className="up-dup-choice__actions">
                      <button type="button" className="btn btn-primary" onClick={handleUseExisting}>
                        Use existing
                      </button>
                      <button type="button" className="btn btn-secondary" onClick={handleUploadAsNew}>
                        Upload as new
                      </button>
                      <button type="button" className="btn btn-link" onClick={() => { setDuplicatePrompt(null); resetState(); }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Workflow started — batch */}
              {uploadStatus === 'batch-workflow-started' && (
                <div className="up-status up-status--green">
                  <span>✓ Workflows started for {batchDocCount} document{batchDocCount !== 1 ? 's' : ''}! Redirecting to processing monitor...</span>
                </div>
              )}

              {/* Error */}
              {uploadStatus === 'error' && error && (
                <div className="up-status up-status--red">
                  <div className="up-error-content">
                    <strong>Upload failed</strong>
                    <p>{error.message}</p>
                    {error.status && <p className="up-error-detail">Status: {error.status}</p>}
                  </div>
                  <button className="btn btn-secondary up-btn-reset" onClick={resetState}>
                    Try Again
                  </button>
                </div>
              )}

              {/* Progress Steps */}
              {uploadStatus === 'uploading' && (
                <div className="up-steps">
                  <Step done={false} active={true} label="Upload file" />
                  <Step done={false} active={false} label="Start workflow" />
                </div>
              )}

              {/* View document link if processing errored but we have a docId */}
              {documentId && uploadStatus === 'error' && (
                <button
                  className="btn btn-secondary"
                  style={{ width: '100%', marginTop: '1rem' }}
                  onClick={() => navigate('/processing')}
                >
                  View Document Anyway
                </button>
              )}
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

// ── Step indicator sub-component ──
function Step({ done, active, label }) {
  return (
    <div className={`up-step${done ? ' up-step--done' : ''}${active ? ' up-step--active' : ''}`}>
      <div className="up-step-icon">
        {done ? (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        ) : active ? (
          <div className="up-spinner-sm" />
        ) : (
          <div className="up-step-dot" />
        )}
      </div>
      <span dangerouslySetInnerHTML={{ __html: label }} />
    </div>
  );
}

export default UploadPage;
